<template>
  <div :class="['wp-form-builder', { 'wp-form-builder--submittable': showSubmitButton }]">
    <div
      v-if="hasErrors"
      class="alert alert-danger"
    >
      Er zijn fouten opgetreden, zie hieronder:
    </div>
    <div
      v-if="editable"
      class="wp-form-builder__toolbar"
    >
      <label>Onderdeel toevoegen:</label>
      <button
        v-for="field in formFields"
        :key="field.value"
        class="btn btn-default btn-sm"
        @click.prevent="addField(field.value)"
      >
        {{ field.text }}
      </button>
    </div>

    <div class="wp-form-builder__container">
      <p
        v-if="userData.length === 0 && environment !== 'registration-pdf'"
        class="wp-form-builder__empty"
      >
        Er zijn nog geen formulieronderdelen.
      </p>

      <form-modal
        v-if="modal"
        v-bind="modal"
        @save="saveModal"
        @cancel="closeModal"
      />

      <draggable
        v-if="editable"
        v-model="userData"
        handle=".wp-draggable__handle"
      >
        <div
          v-for="(field, i) in fields"
          :key="i"
          class="wp-form-builder__item"
        >
          <div class="wp-form-builder__item-index">
            {{ i + 1 }}
          </div>
          <component
            :is="field.type"
            v-bind="field"
            environment="builder"
          />
          <div class="wp-form-builder__item-actions">
            <i
              class="fa fa-cog"
              @click="openModal(field)"
            />
            <i
              class="fa fa-trash"
              @click="remove(field)"
            />
            <i class="wp-draggable__handle fa fa-bars" />
          </div>
        </div>
      </draggable>
      <div
        v-else
      >
        <div
          v-for="field in fields"
          :key="field.name"
          class="wp-form-builder__block"
        >
          <component
            :is="field.type"
            v-model="userData[field.name]"
            v-bind="field"
            :error="errors[field.name]"
            :in-modal="inModal"
            :show-submit-button="showSubmitButton"
            :assigns="assigns"
            :environment="environment"
            @setRequired="(v) => (userData.required = v)"
          />
        </div>
      </div>
    </div>
    <div
      v-if="!editable && showSubmitButton"
      class="form-actions"
    >
      <input
        type="submit"
        name="commit"
        value="Formulier bewaren"
        class="btn"
        data-disable-with="Formulier bewaren"
        data-turbo="false"
        @click="(e) => handleSubmit(e)"
      >
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import uuid from 'uuid';
import FormCheckbox from './form-builder/checkbox.vue';
import FormChips from './form-builder/chips.vue';
import FormDescription from './form-builder/description.vue';
import FormDoubleDescription from './form-builder/double-description.vue';
import FormImage from './form-builder/image.vue';
import FormModal from './form-builder/modal.vue';
import FormRadio from './form-builder/radio.vue';
import FormSelect from './form-builder/select.vue';
import FormSignature from './form-builder/signature.vue';
import FormText from './form-builder/text.vue';
import FormTextarea from './form-builder/textarea.vue';
import FormTitle from './form-builder/title.vue';

const formFields = [
  { text: 'Titel', value: 'FormTitle' },
  { text: 'Alinea', value: 'FormDescription' },
  { text: 'Dubbele alinea', value: 'FormDoubleDescription' },
  { text: 'Tekstveld', value: 'FormText' },
  { text: 'Groot tekstveld', value: 'FormTextarea' },
  { text: 'Radio', value: 'FormRadio' },
  { text: 'Checkbox', value: 'FormCheckbox' },
  { text: 'Select', value: 'FormSelect' },
  { text: 'Handtekening', value: 'FormSignature' },
  { text: 'Image', value: 'FormImage' },
];

export default {
  name: 'FormBuilder',

  components: {
    draggable,
    FormCheckbox,
    FormChips,
    FormDescription,
    FormDoubleDescription,
    FormModal,
    FormRadio,
    FormSelect,
    FormSignature,
    FormText,
    FormTextarea,
    FormTitle,
    FormImage,
  },

  props: {
    schema: {
      type: Array,
      validator(value) {
        return value === null || Array.isArray(value);
      },
      default: null,
    },
    value: {
      type: [Object, Array],
      default: () => [],
    },
    assigns: {
      type: Object,
      default: () => ({}),
    },
    showSubmitButton: {
      type: Boolean,
      default: true,
    },
    environment: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      active: 0,
      modal: null,
      errors: {},
    };
  },

  computed: {
    hasErrors() {
      return Object.keys(this.errors).length;
    },
    userData: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    formFields() {
      return formFields;
    },
    editable() {
      return !this.schema && this.environment !== 'registration-pdf';
    },
    inModal() {
      return !this.editable && !this.showSubmitButton;
    },
    fields: {
      get() {
        if (this.schema && this.environment === 'registration-pdf' && this.schema[0] && this.schema[0].type === 'FormTitle') return this.schema.splice(1); // eslint-disable-line
        if (this.schema) {
          return this.schema.map((field) => {
            const required = (Array.isArray(field.required) ? field.required[0] : field.required) || false; // eslint-disable-line
            return Object.assign(field, { required });
          });
        }
        return this.userData.map((field) => {
          const { schema } = this.$options.components[field.type];
          const required = (Array.isArray(field.required) ? field.required[0] : field.required) || false; // eslint-disable-line
          return Object.assign(field, { schema, required });
        });
      },
    },
  },

  watch: {
    value: {
      handler(val) {
        this.$emit('input', val);
      },
      deep: true,
    },
  },

  methods: {
    // For filling the form structure
    handleSubmit(e) {
      this.validateInput();
      if (this.hasErrors) {
        e.preventDefault();
      }
    },
    validateInput() {
      this.errors = this.schema.reduce((errors, field) => {
        // Check for required field
        if (
          field.required
          && (!this.value[field.name] || this.value[field.name].length === 0)
        ) {
          errors[field.name] = { input: 'moet opgegeven zijn.' };
        }
        // Check for required values
        if (
          field.requiredAnswer
          && (!this.value[field.name]
            || this.value[field.name] !== field.requiredAnswer)
        ) {
          errors[field.name] = {
            ...errors[field.name],
            field: `de optie "${field.requiredAnswer}" moet gekozen zijn.`,
          };
        }
        return errors;
      }, {});
    },

    // For creation of form_structure
    remove({ name }) {
      this.userData = this.userData.filter(field => field.name !== name);
    },
    addField(type) {
      const name = uuid();
      const { schema } = this.$options.components[type];
      const values = Object.assign(
        {},
        ...schema.map(field => ({ [field.name]: field.default })),
      ); // eslint-disable-line
      const field = {
        name,
        type,
        ...values,
      };
      this.userData = [...this.userData, field];
    },
    closeModal() {
      this.modal = null;
    },
    isClick(e) {
      return !e.timeStamp;
    },
    openModal(field) {
      const { schema, ...value } = field;
      this.modal = {
        schema,
        value,
        showSubmitButton: false,
      };
    },
    saveModal(newValue) {
      const i = this.userData.findIndex(
        field => field.name === newValue.name,
      );
      const newUserData = [...this.userData];
      newUserData[i] = newValue;
      this.userData = newUserData;
      this.closeModal();
    },
  },
};
</script>

<style lang="scss">
.wp-form-builder {
  position: relative;
  max-width: 1170px;

  &--submittable {
    margin: 0 auto;
  }

  label {
    margin-bottom: 0;
  }

  &__container {
    position: relative;

    & > div {
      margin-bottom: 10px;
    }
  }

  &__toolbar {
    align-items: stretch;
    background: #123a5e;
    display: flex;
    margin-bottom: 20px;

    label {
      border-right: 1px solid #e8e8e8;
      color: #fff;
      font-size: 13px;
      font-weight: 600;
      margin: 0;
      padding: 10px;
    }

    .btn {
      border-right: 1px solid #e8e8e8;
    }
  }

  &__block {
    position: relative;
    page-break-inside: avoid;
    -webkit-column-break-inside: avoid;
    margin-bottom: 20px;
  }

  &__item {
    border-bottom: 1px solid #e8e8e8;
    display: flex;
    padding: 10px 0;

    &-index {
      color: #123a5e;
      font-weight: bold;
      line-height: 30px;
      text-align: center;
      position: absolute;
      width: 30px;
    }

    &-actions {
      i {
        cursor: pointer;
        font-size: 14px;

        & + i {
          margin-left: 12px;
        }

        &.fa-cog {
          &:hover {
            color: #009fe3;
          }
        }

        &.fa-trash {
          &:hover {
            color: #c2bd00;
          }
        }

        &.fa-bars {
          cursor: grab;
          color: #999;

          &:hover {
            color: #333;
          }
        }
      }
    }
  }

  &__empty {
    font-size: 14px;
    margin: 20px 0 10px;
    text-align: center;
  }
}
</style>
